/**
 * PASEK NAKŁADKI „OGLĄDASZ KONTO …" (BC-10; PRZEBUDOWA 2026-09-19; TREŚĆ POPRAWIONA BF-1).
 *
 * Właściciel: „chcę mieć tylko pasek na samej górze, który pokaże mi opcję powrotu do swojego konta"
 * oraz — po wypadku z 19.09 — „ja nie miałem się na kogoś logować […] to na moim koncie miałem widzieć
 * czyjś widok aplikacji 1 do 1".
 *
 * 🔴 BF-1 (19.09 rano): PASEK MÓWIŁ NIEPRAWDĘ. Stało tu zdanie „dane w modułach są Twoje" — prawdziwe
 * do czasu, gdy nakładka kładła wyłącznie układ. Właściciel poprosił o „1 do 1, nawet dashboard ma być
 * jej wczytany", więc serwer odpowiada teraz danymi osoby oglądanej. Zdanie zostało wymienione, bo pasek,
 * który opisuje poprzednią wersję zachowania, jest gorszy niż brak paska: człowiek podejmuje na jego
 * podstawie decyzję, czyje dane właśnie czyta.
 *
 * CO TU JEST, A CZEGO NIE MA — świadomie:
 *  • czyj widok jest nałożony, ile czasu zostało i JEDEN przycisk wyjścia;
 *  • zdanie, które mówi PRAWDĘ o tym, co widać: nałożony jest CAŁY widok tamtej osoby — układ (menu,
 *    kafelki, uprawnienia, kolory) ORAZ dane w modułach (pulpit, listy, liczniki, treści kierowane do niej);
 *  • OSTRZEŻENIE O POSZERZENIU ZASIĘGU: jeżeli ona ma wejścia, których oglądający NIE MA na swoim koncie,
 *    w tym trybie je zobaczy. Tego nie wolno chować — wejście stoi za imiennym kluczem, każde żądanie
 *    ląduje w dzienniku pod nazwiskiem oglądającego, a pracownik dostał powiadomienie;
 *  • rozwijane „co wolno w tym trybie" — granice przychodzą Z SERWERA (`granice`), ekran ich nie wymyśla;
 *  • NIE MA tu niczego z panelu administratora poza wyjściem.
 *
 * ODLICZANIE JEST TYLKO INFORMACJĄ. O końcu nakładki decyduje serwer (wiersz sesji w bazie), a nie ten
 * licznik — po jego wyzerowaniu pasek zmienia treść i prosi o wyjście, ale niczego sam nie przerywa.
 * Wygaśnięcie nie zabiera administratorowi niczego: przestaje działać nakładka, nie zalogowanie.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiEye, FiArrowLeft, FiClock, FiAlertTriangle, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useAuth } from "../../hooks/useAuth";
import s from "./PasekPodgladu.module.css";

/** Ile pełnych minut zostało do końca sesji (0 gdy już po czasie albo brak danych). */
export const minutyDoKonca = (wygasa, teraz = Date.now()) => {
    if (!wygasa) return 0;
    const koniec = new Date(wygasa).getTime();
    if (Number.isNaN(koniec)) return 0;
    return Math.max(0, Math.ceil((koniec - teraz) / 60000));
};

/* Zapasowa lista na wypadek, gdyby serwer nie przysłał `granice` (stara zakładka, błąd sieci).
   Musi mówić TO SAMO co TrybPodgladuKonta.Granice — zapadka `pasekPodgladu` porównuje treść. */
const GRANICE_DOMYSLNE = [
    "Jesteś zalogowany na SWOIM koncie — to nakładka na widok, nie logowanie na cudze konto.",
    "Widzisz DANE tej osoby, nie swoje — także te, których nie widzisz na własnym koncie.",
    "Tryb jest TYLKO DO ODCZYTU — serwer odrzuca żądania zapisu.",
    "Wyłączone są: sygnalista, e-teczka, wydane dokumenty kadrowe i historia zatrudnienia, treść wniosków kadrowych i sprawy kadrowe, sejf haseł oraz prywatna poczta, kalendarz, OneDrive i czat Microsoft 365.",
    "Każde otwarcie ekranu trafia do dziennika pod TWOIM nazwiskiem, a właściciel konta dostał powiadomienie.",
];

const PasekPodgladu = () => {
    const { podglad, wrocNaSwojeKonto } = useAuth();
    const [zostalo, setZostalo] = useState(() => minutyDoKonca(podglad?.wygasa));
    const [rozwiniete, setRozwiniete] = useState(false);
    const [wraca, setWraca] = useState(false);
    const [blad, setBlad] = useState(null);

    useEffect(() => {
        if (!podglad?.aktywny) return undefined;
        setZostalo(minutyDoKonca(podglad.wygasa));
        const t = setInterval(() => setZostalo(minutyDoKonca(podglad.wygasa)), 15000);
        return () => clearInterval(t);
    }, [podglad]);

    /* Wyjście MUSI zadziałać zawsze. `wrocNaSwojeKonto` gasi nakładkę także wtedy, gdy serwer nie
       odpowiedział — komunikat jest wtedy informacją, a nie błędem blokującym. Przycisk odblokowujemy
       bezwarunkowo: administrator nie ma prawa utknąć przy „Wyłączam…". */
    const wroc = useCallback(async () => {
        setWraca(true);
        setBlad(null);
        const wynik = await wrocNaSwojeKonto();
        setWraca(false);
        if (wynik?.message) setBlad(wynik.message);
        /* [polecenie właściciela 2026-09-19] „jak wracam do swojego widoku, to przywracaj mnie do miejsca
           gdzie byłem wcześniej". Adres przychodzi z SERWERA (zapamiętany przy wejściu), więc powrót działa
           też po odświeżeniu strony i z drugiej karty.

           DLACZEGO PEŁNE PRZEJŚCIE, A NIE NAWIGACJA W APLIKACJI. Przez cały czas trwania nakładki strony
           pobierały dane OGLĄDANEJ osoby i trzymają je w swoich stanach. Odświeżenie tożsamości naprawia
           powłokę, ale nie czyści tego, co każda strona zdążyła sobie zapamiętać — po miękkim przejściu
           administrator zobaczyłby na swoim koncie resztki cudzych danych. Pełne wczytanie jest o ułamek
           sekundy wolniejsze i daje pewność, że wraca WYŁĄCZNIE jego widok.
           (Przy okazji: hak nawigacji wymagałby Routera wokół tego komponentu — pasek ma działać wszędzie.) */
        if (wynik?.sciezkaPowrotu
            && typeof window !== "undefined"
            && wynik.sciezkaPowrotu !== window.location.pathname + window.location.search) {
            window.location.assign(wynik.sciezkaPowrotu);
        }
    }, [wrocNaSwojeKonto]);

    const granice = useMemo(
        () => (Array.isArray(podglad?.granice) && podglad.granice.length ? podglad.granice : GRANICE_DOMYSLNE),
        [podglad],
    );

    if (!podglad?.aktywny) return null;

    const konczySie = zostalo <= 5;

    return (
        <div className={s.pasek} role="region" aria-label="Tryb podglądu cudzego konta">
            <div className={s.rzad}>
                <span className={s.ikona} aria-hidden="true"><FiEye /></span>
                <span className={s.tresc}>
                    <strong className={s.etykieta}>Oglądasz konto:</strong>{" "}
                    <span className={s.osoba}>{podglad.ogladaneNazwa || `konto #${podglad.ogladaneUserId}`}</span>
                    <span className={s.opis}>
                        {" "}— widzisz jej widok i JEJ DANE: pulpit, listy, liczniki i treści kierowane do niej,
                        także te, których nie widzisz na własnym koncie. Jesteś dalej zalogowany na swoim koncie,
                        tryb jest tylko do odczytu, a każde wejście zapisujemy w dzienniku pod Twoim nazwiskiem.
                        Właściciel konta został powiadomiony.
                    </span>
                </span>

                <span className={`${s.zegar} ${konczySie ? s.zegarKoniec : ""}`} title="Twardy limit sesji podglądu">
                    <FiClock aria-hidden="true" />
                    {zostalo > 0 ? `pozostało ${zostalo} min` : "czas minął — nakładka zaraz zgaśnie"}
                </span>

                <button
                    type="button"
                    className={s.szczegoly}
                    onClick={() => setRozwiniete((v) => !v)}
                    aria-expanded={rozwiniete}
                >
                    {rozwiniete ? <FiChevronUp aria-hidden="true" /> : <FiChevronDown aria-hidden="true" />}
                    Co wolno w tym trybie
                </button>

                <button type="button" className={s.powrot} onClick={wroc} disabled={wraca}>
                    <FiArrowLeft aria-hidden="true" />
                    {wraca ? "Wyłączam…" : "Wróć do swojego widoku"}
                </button>
            </div>

            {rozwiniete && (
                <ul className={s.lista}>
                    {granice.map((g) => <li key={g}>{g}</li>)}
                </ul>
            )}

            {blad && (
                <p className={s.blad} role="alert">
                    <FiAlertTriangle aria-hidden="true" /> {blad}
                </p>
            )}
        </div>
    );
};

export default PasekPodgladu;
